import { ReactElement, useEffect } from "react";

import { Actions, AuthState, State } from "./types";

type Props = Readonly<{
  actions: Actions;
  children: ReactElement;
  state: State;
}>;

const USER_TOKEN_STORAGE_KEY = "user token";

export const AuthManager = ({
  actions: { loginWithToken, logout },
  children,
  state: { auth },
}: Props) => {
  useEffect(() => {
    switch (auth.state) {
      case AuthState.initial: {
        // check the URI for a ?token= query parameter first
        const querySearch = new window.URLSearchParams(window.location.search);
        let authString = null;
        if (querySearch.has("token")) {
          authString = querySearch.get("token");
        } else if (window.location.hash.length > 0) {
          const tokenPosition = window.location.hash.indexOf("token=");
          if (tokenPosition >= 0) {
            let nextAmpersand = window.location.hash.indexOf("&", tokenPosition);
            if (nextAmpersand < 0) {
              nextAmpersand = window.location.hash.length;
            }
            authString = window.location.hash.substr(
              tokenPosition + "token=".length,
              nextAmpersand,
            );
          }
        }
        if (authString && authString.length > 0) {
          try {
            // auth strings are base64 encoded and suffixed with a colon
            const split = window.atob(authString).split(":");
            if (split.length === 2) {
              loginWithToken(split[0]);
              return;
            }
          } catch (err) {
            console.error(err);
          }
        }
        // check local storage for a saved token
        const storedToken = window.localStorage.getItem(USER_TOKEN_STORAGE_KEY);
        if (storedToken && storedToken.length > 0) {
          loginWithToken(storedToken);
        } else {
          logout(); // mark the state as not logged in
        }
        break;
      }
      case AuthState.notLoggedIn: {
        window.localStorage.removeItem(USER_TOKEN_STORAGE_KEY);
        break;
      }
      case AuthState.loggedIn: {
        window.localStorage.setItem(USER_TOKEN_STORAGE_KEY, auth.token);
        break;
      }
    }
  }, [auth]);
  return children;
};
AuthManager.displayName = "AuthManager";
